import { theme } from '@prophecy/theme';
import { usePersistentCallback } from '@prophecy/utils/react/hooks';
import React, { useState, useRef, RefObject } from 'react';
import styled from 'styled-components';

import { Stack } from '../Layout';
import { HintPropsType } from '../Misc/Hint';
import { Tooltip, TooltipProps } from '../Tooltip';
import { LabelWithHint } from '../Typography/LabelWithHint';

export const StyledContent = styled.div`
  display: initial;
`;

const ContentWrapper = styled.div`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  ${StyledContent} {
    /** 
     Browser can add ellipsis when the character barely touch the container.
     This padding adds an offset to handle such scenario  */
    padding-right: 0.2ch;
  }
`;

export const overlayStyle: React.CSSProperties = {
  maxWidth: '500px',
  maxHeight: '300px',
  overflow: 'auto',
  padding: theme.spaces.x12,
  wordBreak: 'break-all'
};

type EllipsisProps = React.HTMLAttributes<HTMLInputElement> & {
  tooltip?: boolean;
  tooltipProps?: Omit<TooltipProps, 'children'>;
  children: React.ReactNode;
  hint?: HintPropsType;
  containerRef?: RefObject<HTMLDivElement>;
  alwaysShowTooltip?: boolean;
  onTooltipChange?: (hasTooltip: boolean) => void;
};

const ChildrenWithHint = ({
  hint,
  content,
  className
}: {
  hint?: HintPropsType;
  content: React.ReactNode;
  className?: string;
}) => {
  if (!hint) {
    return <>{content}</>;
  }
  return (
    <Stack width='100%' direction='horizontal' alignY='center' gap={theme.spaces.x8} className={className}>
      {content}
      <LabelWithHint hint={hint}>{null}</LabelWithHint>
    </Stack>
  );
};

export function Ellipsis({
  tooltip,
  hint,
  tooltipProps,
  children,
  containerRef,
  onTooltipChange,
  alwaysShowTooltip,
  ...rest
}: EllipsisProps) {
  const [showTooltip, toggleTooltip] = useState<boolean>(false);
  const ref = useRef<HTMLDivElement | null>(null);
  const currentRef = containerRef || ref;
  const handleTruncate = usePersistentCallback((wrapper: HTMLDivElement | null) => {
    let hasEllipsis = false;
    if (wrapper) {
      const { width } = wrapper.getBoundingClientRect();
      const { width: childWidth } = wrapper.children[0].getBoundingClientRect();
      if (width < childWidth) {
        hasEllipsis = true;
      }
    }
    return hasEllipsis;
  });
  const handleTooltipTitleChange = usePersistentCallback(() => {
    if (handleTruncate(ref.current)) {
      toggleTooltip(true);
      onTooltipChange?.(true);
    } else if (showTooltip) {
      toggleTooltip(false);
      onTooltipChange?.(false);
    }
  });

  if (!tooltip)
    return (
      <ChildrenWithHint
        hint={hint}
        content={
          <ContentWrapper {...rest} ref={currentRef}>
            {children}
          </ContentWrapper>
        }
        className={rest?.className}
      />
    );

  const tooltipTitle = tooltipProps?.title || children;
  const content = (
    <ContentWrapper {...rest} ref={currentRef} onMouseEnter={handleTooltipTitleChange}>
      <StyledContent>{children}</StyledContent>
    </ContentWrapper>
  );

  // Only wrap in tooltip when needed to avoid conflicts with nested tooltips.
  // Always wrapping causes issues when parent and child elements both have tooltips. Example: graph node.
  const tooltipUI =
    showTooltip || alwaysShowTooltip ? (
      <Tooltip {...tooltipProps} title={tooltipTitle}>
        {content}
      </Tooltip>
    ) : (
      content
    );

  return <ChildrenWithHint hint={hint} content={tooltipUI} className={rest?.className} />;
}
